import { z, type ZodObject, type ZodRawShape } from 'zod';

import { isValidE164 } from '../utils/phone';
import type { ConversationConfig } from './conversation';

/**
 * Form field descriptor generated at runtime from a Zod schema.
 * Used by the landing page form renderer.
 */
export interface FormFieldDescriptor {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'enum' | 'array';
  required: boolean;
  enumValues?: string[];
  description?: string;
}

/**
 * Convert a Zod object schema into an array of form field descriptors at runtime.
 * This replaces the old JSON descriptor approach and keeps Zod as the single source of truth.
 */
export function zodToFormFields(
  schema: ZodObject<ZodRawShape>,
): FormFieldDescriptor[] {
  const shape = schema.shape;
  const fields: FormFieldDescriptor[] = [];

  for (const [name, fieldSchema] of Object.entries(shape)) {
    let innerSchema = fieldSchema as z.ZodTypeAny;
    let required = true;

    // Unwrap optional (supports both Zod 3 and Zod 4)
    if (
      'unwrap' in innerSchema &&
      innerSchema.constructor.name.includes('Optional')
    ) {
      required = false;
      innerSchema = (innerSchema as z.ZodOptional<z.ZodTypeAny>).unwrap();
    }

    // Unwrap default (supports both Zod 3 and Zod 4)
    if (
      '_def' in innerSchema &&
      innerSchema.constructor.name.includes('Default')
    ) {
      required = false;
      innerSchema = (innerSchema as z.ZodDefault<z.ZodTypeAny>)._def.innerType;
    }

    const descriptor: FormFieldDescriptor = { name, type: 'string', required };

    const schemaName = innerSchema.constructor.name;

    if (schemaName.includes('String')) {
      descriptor.type = 'string';
    } else if (schemaName.includes('Number')) {
      descriptor.type = 'number';
    } else if (schemaName.includes('Boolean')) {
      descriptor.type = 'boolean';
    } else if (schemaName.includes('Enum')) {
      descriptor.type = 'enum';
      // Support both Zod 3 (.options) and Zod 4 (.entries / ._def.entries)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const enumSchema = innerSchema as any;
      descriptor.enumValues = ((enumSchema.options as string[]) ??
        (enumSchema.entries as string[]) ??
        Object.values(
          (enumSchema._def as Record<string, unknown>)?.entries ?? {},
        )) as string[];
    } else if (schemaName.includes('Array')) {
      descriptor.type = 'array';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const elementType =
        (innerSchema as any).element ?? (innerSchema as any)._def?.innerType;
      if (elementType?.constructor?.name?.includes('Enum')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const elemEnum = elementType as any;
        descriptor.enumValues = ((elemEnum.options as string[]) ??
          (elemEnum.entries as string[]) ??
          Object.values(
            (elemEnum._def as Record<string, unknown>)?.entries ?? {},
          )) as string[];
      }
    }

    if (innerSchema.description) {
      descriptor.description = innerSchema.description;
    }

    fields.push(descriptor);
  }

  return fields;
}

/**
 * Build a combined Zod form schema from pre-computed field descriptors.
 *
 * Used client-side to reconstruct validation from serializable descriptors
 * (avoids passing Zod class instances across the server/client boundary).
 *
 * Phone is required by default. Verticals that capture leads without a
 * phone number (e.g. B2B email-only flows) can pass `requirePhone: false`
 * to relax the check while still validating format when a value is present.
 */
export function formFieldsToZodSchema(
  fields: FormFieldDescriptor[],
  options: { requirePhone?: boolean } = {},
): ZodObject<ZodRawShape> {
  const requirePhone = options.requirePhone ?? true;

  // Trim before validating so that whitespace-only input (e.g. "  ") is
  // treated as empty and fails the min(1) check instead of slipping past.
  const phoneSchema = requirePhone
    ? z
        .string()
        .trim()
        .min(1, 'Phone number is required')
        .refine((val) => isValidE164(val.startsWith('+') ? val : `+${val}`), {
          message:
            'Enter a valid phone number with country code (e.g. +27 82 123 4567)',
        })
    : z
        .string()
        .trim()
        .optional()
        .or(z.literal(''))
        .refine(
          (val) => !val || isValidE164(val.startsWith('+') ? val : `+${val}`),
          {
            message:
              'Enter a valid phone number with country code (e.g. +27 82 123 4567)',
          },
        );

  const coreSchema = z.object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    email: z
      .string()
      .email('Please enter a valid email address')
      .optional()
      .or(z.literal('')),
    phone: phoneSchema,
    consentGiven: z.literal(true, {
      message: 'You must provide consent to continue',
    }),
    consentText: z.string().optional(),
  });

  const customShape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    let fieldSchema: z.ZodTypeAny;

    switch (field.type) {
      case 'boolean':
        // Do NOT default to `false`. Checkboxes that the user never clicks
        // stay `undefined` in form state, and the scoring classifier's
        // `requiredFieldsPopulated` gate treats that as "unknown" (hold
        // the lead in nurturing) instead of "no" (auto-disqualify). The
        // previous `.default(false)` coerced every unclicked checkbox to
        // an explicit "no", which made 14/14 recent drivers landing-page
        // submissions score=8 and hard-disqualify on the
        // hasDriversLicense rule. Matches the vertical-level schema
        // flipped in packages/verticals/drivers/src/config.ts.
        fieldSchema = z.boolean();
        break;
      case 'number':
        fieldSchema = z.coerce.number();
        break;
      case 'enum':
        if (field.enumValues && field.enumValues.length > 0) {
          fieldSchema = z.enum(field.enumValues as [string, ...string[]]);
        } else {
          fieldSchema = z.string();
        }
        break;
      default:
        fieldSchema = z.string();
    }

    if (!field.required) {
      fieldSchema = fieldSchema.optional();
    }

    customShape[field.name] = fieldSchema;
  }

  return coreSchema.merge(
    z.object({ customFields: z.object(customShape) }),
  ) as ZodObject<ZodRawShape>;
}

/**
 * Compliance jurisdiction types supported by the platform.
 */
export const ComplianceJurisdictionSchema = z.enum([
  'us-tcpa',
  'eu-gdpr',
  'ch-dsg',
  'ch-dsg-health',
  'za-popia',
  'ng-ndpr',
  'ke-dpa',
  'br-lgpd',
  'in-dpdp',
  'ph-dpa',
]);

export type ComplianceJurisdiction = z.infer<
  typeof ComplianceJurisdictionSchema
>;

/**
 * Outreach channel types.
 */
export const OutreachChannelSchema = z.enum([
  'email',
  'sms',
  'whatsapp',
  'instagram',
  'phone',
  'linkedin',
]);

export type OutreachChannel = z.infer<typeof OutreachChannelSchema>;

/**
 * Delivery adapter types for sending qualified leads to clients.
 */
export const DeliveryAdapterSchema = z.enum([
  'webhook',
  'email',
  'crm_api',
  'csv_export',
  'supabase',
  // `uber_hero` is the referral / commission product (ORGANIZATION_TYPE_HERO).
  // The legacy `uber_supplier` fleet adapter was retired 2026-07-05 (0
  // conversions, status sync dead since 2026-04-28). Verdict:
  // docs/investigations/uber-supplier-channel-verdict/findings-2026-07-05.md.
  'uber_hero',
]);

export type DeliveryAdapter = z.infer<typeof DeliveryAdapterSchema>;

/**
 * Scoring rule definition: field-based conditions that contribute to the lead score.
 */
export const ScoringRuleSchema = z.object({
  field: z.string(),
  operator: z.enum([
    'eq',
    'neq',
    'gt',
    'gte',
    'lt',
    'lte',
    'contains',
    'exists',
    'days_until_lte',
  ]),
  value: z.unknown(),
  points: z.number(),
  description: z.string().optional(),
  required: z.boolean().optional(),
});

export type ScoringRule = z.infer<typeof ScoringRuleSchema>;

/**
 * Scoring configuration: rules, AI prompt template, and threshold definitions.
 */
export const ScoringConfigSchema = z.object({
  rules: z.array(ScoringRuleSchema),
  aiPromptTemplate: z.string().optional(),
  thresholds: z.object({
    qualified: z.number(),
    nurture: z.number(),
    discard: z.number(),
  }),
});

export type ScoringConfig = z.infer<typeof ScoringConfigSchema>;

/**
 * Outreach sequence step definition.
 */
export const SequenceStepSchema = z.object({
  channel: OutreachChannelSchema,
  delayMinutes: z.number().nonnegative(),
  templateId: z.string(),
  condition: z.string().optional(),
  /**
   * Optional free-form WhatsApp body builder (whatsapp steps only).
   *
   * Together with `templateId`, this is the transport-agnostic message content
   * of a whatsapp step (ADR: platform-agnostic-message-content). The sender
   * gateway derives a `MessageContent` from the step — `templateId` → the Meta
   * `waTemplate` (+ its cap-tier category); this `body` builder → the free-form
   * text — and `selectRepresentation` picks ONE per the RESOLVED session's
   * transport provider (from `waba_numbers`):
   *   - `baileys` / `sms_telnyx` → the `body` as free-form text (Baileys cannot
   *     send `templateMessage`; a step with NO `body` cleanly GATES — a logged
   *     skip, never a silent template-over-Baileys failure).
   *   - `meta_cloud_api` → the approved Meta template (falls back to `body`).
   *
   * So a step carries BOTH representations from ONE config: a Baileys-only
   * vertical (e.g. drivers, `+41765022800`) delivers the body as text, and the
   * SAME step sends the template automatically if the vertical regains a
   * Cloud-API session — no re-authoring.
   *
   * The body MUST be SERVICE-scope (a transactional follow-up to the lead's own
   * application) and free of the marketing lexicon the sender-gateway's
   * category-at-send guard scans for (`findMarketingLexicon`: earnings, "get
   * started", "welcome", "offer", …) or the gateway blocks the send as
   * `category_mismatch`. `templateId` is ALWAYS retained (attribution, sequence
   * idempotency, per-recipient cap category) — never removed.
   */
  body: z
    .custom<(lead: FirstMessageLead) => string>(
      (val) => typeof val === 'function',
      { message: 'SequenceStep.body must be a function (lead) => string' },
    )
    .optional(),
  /**
   * Explicit template category for Rail 2 (per-recipient 24h marketing cap)
   * and Rail 6 (marketing opt-in scope). When set, the outreach executor uses
   * it INSTEAD of the `getTemplateCategory` ID-substring heuristic.
   *
   * Why it exists: the heuristic classifies any templateId containing
   * "welcome" as MARKETING. That made `rideshare-backlog-recovery`'s single
   * step (templateId `rideshare-driver-welcome-whatsapp`, an explicitly
   * SERVICE-scope transactional opener) demand `whatsapp_marketing` consent no
   * Meta Lead Ads lead has — the lane was blocked 100% from birth (verified on
   * prod 2026-08-10: 0 sent rows ever, 56/56 leads skipped
   * `marketing_scope_not_granted`). The gateway already sends the same body as
   * `service`; only the executor pre-gate misclassified it.
   *
   * Leave unset to keep the heuristic (correct for genuinely promotional
   * templates). Set it only with the same care as the SERVICE-scope rationale
   * required on `body` above — declaring UTILITY on a promotional step would
   * bypass Rail 6.
   */
  templateCategory: z
    .enum(['MARKETING', 'UTILITY', 'AUTHENTICATION'])
    .optional(),
});

export type SequenceStep = z.infer<typeof SequenceStepSchema>;

/**
 * Outreach sequence: a named series of steps.
 *
 * `triggerOn` marks a sequence as fired by a lifecycle event rather than the
 * regular scoring/qualification pipeline:
 * - undefined (default): sequence is fired by the regular scoring/
 *   qualification pipeline (first sequence in the list wins)
 * - 'delivered': enqueued from the delivery engine immediately after
 *   `transitionToDelivered()` succeeds (post-delivery nurture)
 * - 'stale': the single final-touch the `sweep-stale-leads` cron fires once
 *   before terminalizing a stale pre-conversion lead (re-engage → grace →
 *   disqualify). At most one `triggerOn: 'stale'` sequence per vertical; a
 *   vertical without one keeps the legacy immediate-disqualify sweep. This is
 *   how re-engagement opts in per vertical — the mechanism is pipeline-wide,
 *   the template is vertical-specific.
 */
export const SequenceSchema = z.object({
  id: z.string(),
  name: z.string(),
  steps: z.array(SequenceStepSchema),
  triggerOn: z.enum(['delivered', 'stale']).optional(),
});

export type Sequence = z.infer<typeof SequenceSchema>;

/**
 * Rate limit configuration per channel.
 */
export const RateLimitSchema = z.object({
  channel: OutreachChannelSchema,
  maxPerHour: z.number().positive(),
  maxPerDay: z.number().positive(),
});

export type RateLimit = z.infer<typeof RateLimitSchema>;

/**
 * Send window configuration: when outreach messages can be sent.
 * Canonical definition. Outreach package's SendWindowSchema should import this
 * or stay in sync via contract tests until circular deps are resolved.
 */
export const OutreachSendWindowSchema = z.object({
  days: z.array(z.number().min(0).max(6)),
  startHour: z.number().min(0).max(23),
  endHour: z.number().min(0).max(23),
  timezone: z.string().default('UTC'),
});

export type OutreachSendWindow = z.infer<typeof OutreachSendWindowSchema>;

/**
 * Outreach experiment configuration for A/B testing sequences.
 * Maps variant names to sequence IDs. Allocation is percentage-based.
 */
export const OutreachExperimentSchema = z.object({
  enabled: z.boolean(),
  allocation: z.record(z.string(), z.number()),
});

export type OutreachExperiment = z.infer<typeof OutreachExperimentSchema>;

/**
 * Which ChatSuite session a vertical's WhatsApp sends leave from.
 *
 * `tel` is the E.164 phone of a ChatSuite WhatsApp session, which maps 1:1 to
 * a Meta WABA. Meta templates are WABA-scoped, so a vertical MUST send from a
 * session whose WABA contains its templates. This replaces the single global
 * `CHATSUITE_SESSION_TEL` env var, which forced every vertical through one
 * WABA (the drivers WABA) regardless of which templates it owned.
 *
 * A vertical that omits this cannot send WhatsApp: the resolver returns no
 * session and the send is blocked (never silently routed to another vertical's
 * WABA). See `resolveChatSuiteSession` in @selenos/outreach.
 */
export const ChatSuiteSessionSchema = z.object({
  /** E.164 phone of the ChatSuite session (its own Meta WABA). */
  tel: z.string().regex(/^\+[1-9]\d{6,14}$/, 'tel must be E.164 (e.g. +41…)'),
  /**
   * Declared transport of this session — the DEFAULT the sender gateway assumes
   * for the number when `waba_numbers` has no registry row yet (the empty-table
   * / pre-seed window). `baileys`/`sms_telnyx` (text-only) render a step's
   * free-form `body` and gate a template-only step; `meta_cloud_api` sends the
   * approved template. Omit for an official Cloud-API WABA (the gateway defaults
   * an unknown number to `meta_cloud_api`, the legacy-preserving choice). Set
   * `'baileys'` on a Baileys-only session (e.g. drivers `+41765022800`) so it
   * renders text WITHOUT a migration-ordering dependency. The `waba_numbers`
   * row, once present, always wins (it carries live rotation/lifecycle state).
   */
  transport: z.enum(['baileys', 'meta_cloud_api', 'sms_telnyx']).optional(),
});

export type ChatSuiteSession = z.infer<typeof ChatSuiteSessionSchema>;

/**
 * The minimal lead shape the per-vertical `firstMessage.body` builder reads to
 * personalize the one-shot first message. Kept narrow so a vertical author can
 * write a body builder without coupling to the full `leads` row. The hot-reply
 * lane populates this from the captured lead before firing.
 */
export interface FirstMessageLead {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  email: string | null;
  /** The vertical's captured custom fields (e.g. drivers `city`). */
  customFields: Record<string, unknown>;
}

/**
 * Per-vertical one-shot FIRST MESSAGE config (the Lead-Ads → WhatsApp one-shot).
 *
 * The funnel fires EXACTLY ONE outbound WhatsApp per captured lead (the
 * never-restart invariant), and this is the message. Two transports:
 *
 *   - `transport: 'baileys'` → free-form text sent by ChatSuite/Baileys (no
 *     WABA, no template approval, no Meta opt-in checkbox). `body` builds the
 *     personalized plain-text string from the lead. This is the Selenos default
 *     for whatsapp-first verticals (drivers, spitex) per
 *     `docs/architecture/lead-funnel-systems.md`.
 *   - `transport: 'template'` → a Meta-approved WABA template (`template.name`
 *     + `template.lang`), for verticals that still send via Cloud API.
 *
 * The `HOT_REPLY_WHATSAPP_TEMPLATE_<SLUG>` env var remains an override/fallback
 * (see `handleLeadHotReply`): when set it forces the template path even for a
 * `baileys` vertical, so an operator can fall back to a template without a
 * deploy. A vertical that omits `firstMessage` falls back to the env-template
 * path only.
 */
/**
 * One arm of a first-message opener A/B: a relative `weight` and its own `body`
 * builder. Weights across arms are normalized (see `pickWeightedVariant`), so
 * `{ control: 1, intent: 1 }` is a 50/50 split.
 */
export const FirstMessageVariantSchema = z.object({
  weight: z.number().positive(),
  body: z.custom<(lead: FirstMessageLead) => string>(
    (val) => typeof val === 'function',
    { message: 'variant.body must be a function (lead) => string' },
  ),
});

export type FirstMessageVariant = z.infer<typeof FirstMessageVariantSchema>;

/**
 * Opener A/B experiment on the one-shot first message (baileys transport only).
 *
 * When `enabled`, the hot-reply lane assigns each lead to a variant by a
 * deterministic hash of its UUID and sends that variant's `body` instead of the
 * config's default `body`. The chosen arm + `key` are stamped into the
 * `outreach_log` row's `metadata` (`opener_variant`, `opener_experiment`) so
 * reply-rate can be split by arm at read time. When `enabled` is false the
 * default `body` (the control) is always used — the experiment is inert.
 *
 * `body` (on the parent config) remains REQUIRED for a baileys first message: it
 * is the control/fallback used whenever the experiment is disabled or resolves
 * to no arm.
 */
export const FirstMessageExperimentSchema = z
  .object({
    /** Stable key, stamped into `outreach_log.metadata.opener_experiment`. */
    key: z.string().min(1),
    enabled: z.boolean(),
    /** Arm name -> { weight, body }. Needs at least two arms. */
    variants: z.record(z.string(), FirstMessageVariantSchema),
  })
  .superRefine((cfg, ctx) => {
    if (Object.keys(cfg.variants).length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['variants'],
        message: 'firstMessage.experiment needs at least two variants.',
      });
    }
  });

export type FirstMessageExperiment = z.infer<
  typeof FirstMessageExperimentSchema
>;

export const FirstMessageConfigSchema = z
  .object({
    transport: z.enum(['baileys', 'template']),
    /**
     * Consent scope this first message requires. Drives BOTH the compliance
     * gate (Rail 6) category and the sender-gateway send category:
     *   - `'marketing'` (default) → MARKETING: requires `<channel>_marketing`
     *     opt-in. Use for benefit/earnings-framed one-shots.
     *   - `'service'` → UTILITY: a transactional reply to the lead's own form
     *     submission (acknowledge application, ask an onboarding question).
     *     Skips the marketing-scope check, so a lead who granted only
     *     `<channel>_service` consent (e.g. Meta Lead Ads with no marketing
     *     opt-in) can receive it. The body MUST NOT contain marketing lexicon
     *     (earnings, rates) or the gateway's category-at-send guard blocks it.
     */
    category: z.enum(['service', 'marketing']).default('marketing'),
    /**
     * Free-form text builder. Required when `transport === 'baileys'`. Receives
     * the captured lead and returns the plain-text WhatsApp body (no buttons,
     * no markdown — Baileys interactive elements are unreliable). MUST be a pure
     * function of the lead.
     */
    body: z
      .custom<(lead: FirstMessageLead) => string>(
        (val) => typeof val === 'function',
        { message: 'firstMessage.body must be a function (lead) => string' },
      )
      .optional(),
    /** Meta-approved template. Required when `transport === 'template'`. */
    template: z
      .object({
        name: z.string().min(1),
        lang: z.string().min(1),
      })
      .optional(),
    /**
     * REACTIVE-FIRST WAIT. Minutes to hold the one-shot before sending it, so
     * the lead has a chance to open the conversation themselves.
     *
     * The funnel hands a qualified applicant straight into WhatsApp from the
     * form's thank-you screen (`/wa/<vertical>`). A thread the lead opens is
     * worth roughly an order of magnitude in ban rate against one we open, so
     * the opener should be the fallback, not the race winner. With this set, the
     * hot-reply job is enqueued with `run_after = now + delayMinutes`; when the
     * lead writes in the meantime, `leads.first_inbound_at` is stamped and the
     * job skips instead of sending.
     *
     * Omit (the default) to keep the immediate speed-to-lead behaviour — no
     * vertical changes unless it opts in. 0 is allowed and means "immediate",
     * explicitly rather than by omission.
     */
    delayMinutes: z.number().int().min(0).max(1440).optional(),
    /**
     * Optional opener A/B. Baileys transport only — a `template` first message
     * is Meta-approved copy and cannot be varied per-lead. When set + enabled,
     * overrides the default `body` per assigned arm.
     */
    experiment: FirstMessageExperimentSchema.optional(),
  })
  .superRefine((cfg, ctx) => {
    if (cfg.transport === 'baileys' && !cfg.body) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['body'],
        message:
          "firstMessage.transport 'baileys' requires a `body` builder (lead) => string.",
      });
    }
    if (cfg.transport === 'template' && !cfg.template) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['template'],
        message:
          "firstMessage.transport 'template' requires a `template` { name, lang }.",
      });
    }
    if (cfg.transport === 'template' && cfg.experiment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['experiment'],
        message:
          'firstMessage.experiment is baileys-only (template copy is Meta-approved and cannot be varied per-lead).',
      });
    }
  });

export type FirstMessageConfig = z.infer<typeof FirstMessageConfigSchema>;

/**
 * Outreach configuration: channels, sequences, rate limits, and send window.
 */
export const OutreachConfigSchema = z.object({
  enabledChannels: z.array(OutreachChannelSchema),
  sequences: z.array(SequenceSchema),
  rateLimits: z.array(RateLimitSchema),
  sendWindow: OutreachSendWindowSchema.optional(),
  experiment: OutreachExperimentSchema.optional(),
  /** The ChatSuite session this vertical's WhatsApp sends leave from.
   *  Omit to make the vertical's WhatsApp sends fail-fast (blocked). */
  chatsuiteSession: ChatSuiteSessionSchema.optional(),
  /**
   * The one-shot first message fired on Lead-Ads capture (the
   * Lead Ads → WhatsApp one-shot, never-restart funnel). Omit to fall back to
   * the `HOT_REPLY_WHATSAPP_TEMPLATE_<SLUG>` env-template path.
   */
  firstMessage: FirstMessageConfigSchema.optional(),
  /**
   * Pre-filled text for the reactive click-to-chat handoff (`/wa/<vertical>`),
   * i.e. the message the LEAD sends us when they tap the CTA on the Instant
   * Form thank-you screen. This flips the funnel from proactive (we message a
   * stranger) to reactive (they message us first), which is the single biggest
   * ban-rate lever for unofficial WhatsApp senders.
   *
   * A `wa.me` link without a `?text=` prefill costs 20-30% of replies (see
   * `checkCtaUrl` in @selenos/ad-production), hence the 10-char floor. Written
   * in the LEAD's voice, not ours. Omit to fall back to a generic prefill.
   */
  handoffPrefill: z.string().min(10).optional(),
  /**
   * The ChatSuite AI agent that handles this vertical's EMAIL channel
   * (inbound-email Phase 2). When set, a captured inbound email reply can be
   * forwarded to this agent (gated by the `agent_email_enabled:<slug>` +
   * global `agent_email_kill_switch` operational_flags, both off by default).
   * Reuse the vertical's existing WhatsApp agent id (the ChatSuite agent loop
   * is channel-agnostic). Omit to keep the vertical's email replies on the
   * Phase-1 human/Slack path.
   */
  emailAgent: z
    .object({
      organizationId: z.string().min(1),
      agentId: z.string().uuid(),
    })
    .optional(),
});

export type OutreachConfig = z.infer<typeof OutreachConfigSchema>;

/**
 * Compliance configuration: jurisdiction, consent, and do-not-contact list.
 */
export const ComplianceConfigSchema = z.object({
  jurisdiction: ComplianceJurisdictionSchema,
  /**
   * Marks `jurisdiction` as an intentional cross-border / global choice (e.g. a
   * CH-based global B2B product applying eu-gdpr as a stricter superset rather
   * than its domestic ch-dsg). Suppresses the country↔jurisdiction mismatch
   * warning in business verification. Does NOT relax any runtime consent gate.
   */
  crossBorderJurisdiction: z.boolean().optional(),
  /**
   * When true, the vertical's consent prompt grants MARKETING-category scope
   * (`<channel>_marketing`) in addition to service scope, so the WABA Rail 6
   * marketing-scope gate passes for nurture/welcome sends. Set this ONLY when
   * the consent text the user actually agrees to authorizes ongoing commercial
   * contact (e.g. Selenos: "cool if I follow up with a plan and next steps?").
   * Default (undefined/false) = service-only; we never assume marketing consent.
   */
  marketingConsent: z.boolean().optional(),
  consentRequirements: z.array(z.string()),
  dncEnabled: z.boolean(),
  dpiaRequired: z.boolean().optional(),
  dpiaReference: z.string().optional(),
  dataRetentionMonths: z.number().positive().optional(),
  dataProcessors: z
    .array(
      z.object({
        name: z.string(),
        purpose: z.string(),
        location: z.string(),
      }),
    )
    .optional(),
});

export type ComplianceConfig = z.infer<typeof ComplianceConfigSchema>;

/**
 * Qualification-completeness gate for delivery.
 *
 * Last-line-of-defense guard, evaluated in the delivery engine BEFORE the
 * adapter runs: a lead must have at least `minPopulatedFields` populated
 * fields among `fields` (resolved from core lead columns or `custom_fields`),
 * otherwise delivery is soft-blocked with `pendingReason: 'under_qualified'`
 * and the lead stays non-terminal so re-engagement can collect the missing
 * answers and retry.
 *
 * Vertical-agnostic: a vertical that omits this object is ungated (the gate is
 * a no-op), preserving back-compat. Drivers sets it because Meta Lead Ads
 * leads enter with empty qualification fields yet were reaching `qualified`
 * via an AI-manufactured score and auto-delivering to Uber empty.
 */
/**
 * Delivery prerequisites: qualification fields that must be EXPLICITLY `true`
 * before a lead may be delivered, regardless of score or completeness.
 *
 * Unlike `minPopulatedFields` (which measures completeness, where `false`
 * counts as an answer), a prerequisite measures positivity at the delivery
 * seam: a drivers lead who answered "no vehicle" or "no insurance" is
 * unqualifiable on the client side (Uber), so delivering it only inflates the
 * client's under-qualified dropout. The lead itself is ALWAYS saved and never
 * hard-disqualified at capture ("disqualification is a status, never blocks
 * creation"); the gate soft-blocks delivery with `under_qualified` so the
 * existing field-collection re-processor can recover missing answers, and
 * confirmed-negative leads are tagged as a cohort for future buyers (e.g.
 * fleet partners renting vehicles to carless drivers).
 */
export const DeliveryPrerequisitesSchema = z.object({
  /**
   * Field keys (resolved like gate fields: `custom_fields` first, then core
   * lead columns) that must ALL be `true` for delivery to proceed. A missing
   * answer fails the gate the same as an explicit `false`, but only explicit
   * `false` answers drive the cohort tag below.
   */
  fields: z.array(z.string()).min(1),
  /**
   * `custom_fields` key stamped with the gap cohort tag when at least one
   * prerequisite is confirmed `false` (e.g. `vehicle_gap` for drivers).
   * Cleared again if the lead later satisfies every prerequisite.
   */
  tagField: z.string().min(1),
  /**
   * Tag value per prerequisite field, stamped when exactly that field is
   * confirmed `false` (e.g. `{ hasVehicle: 'no_vehicle' }`).
   */
  tagValueByField: z.record(z.string(), z.string()),
  /** Tag value stamped when more than one prerequisite is confirmed `false`. */
  tagValueAll: z.string().min(1),
});

export type DeliveryPrerequisites = z.infer<typeof DeliveryPrerequisitesSchema>;

export const QualificationGateSchema = z.object({
  /**
   * Field keys that count toward completeness. Each is resolved first against
   * the lead's `custom_fields`, then against core lead columns (e.g. `city`,
   * `phone`). A field counts as populated when present and not null/empty.
   */
  fields: z.array(z.string()).min(1),
  /** Minimum number of `fields` that must be populated to allow delivery. */
  minPopulatedFields: z.number().int().positive(),
  /**
   * Optional positivity gate on top of the completeness gate: fields that
   * must be explicitly `true` for delivery. Omit to keep the gate
   * completeness-only (back-compat).
   */
  prerequisites: DeliveryPrerequisitesSchema.optional(),
});

export type QualificationGate = z.infer<typeof QualificationGateSchema>;

/**
 * Delivery configuration: how qualified leads are sent to clients.
 */
export const DeliveryConfigSchema = z.object({
  adapter: DeliveryAdapterSchema,
  endpoint: z.string().optional(),
  format: z.enum(['json', 'csv', 'xml']).default('json'),
  /**
   * Revenue credited per successful conversion (e.g. Uber activation).
   * Threaded into Meta CAPI `Purchase` events so ad optimisation learns
   * about the revenue event — F3d. Optional because verticals without a
   * fixed per-conversion payout (e.g. free B2B lead-magnet verticals)
   * don't need it. Currency is vertical-local (drivers = ZAR).
   */
  referralValue: z.number().nonnegative().optional(),
  /**
   * Optional qualification-completeness gate. When set, the delivery engine
   * refuses delivery for leads with too few populated qualification fields
   * (soft-block, non-terminal). Omit to leave a vertical ungated.
   */
  qualificationGate: QualificationGateSchema.optional(),
});

export type DeliveryConfig = z.infer<typeof DeliveryConfigSchema>;

/**
 * Landing page theme configuration.
 * Controls the visual identity of vertical-specific landing pages.
 */
export const LandingThemeSchema = z.object({
  primaryColor: z.string(),
  secondaryColor: z.string(),
  accentColor: z.string(),
  heroStyle: z.enum(['dark', 'light', 'gradient']),
  personality: z.enum(['bold', 'professional', 'creative']),
});

export type LandingTheme = z.infer<typeof LandingThemeSchema>;

/**
 * Brand identity for a vertical. Controls favicon, logo, and display name
 * on landing pages. "Leadgen" should never appear to end users.
 */
export const VerticalBrandingSchema = z.object({
  /** Public-facing brand name (e.g. "Rovvo", "Ballee Flow") */
  brandName: z.string(),
  /** Path to favicon.ico (relative to /public) */
  faviconIco: z.string().optional(),
  /** Path to apple-touch-icon.png */
  appleTouchIcon: z.string().optional(),
  /** Path to SVG icon for modern browsers */
  iconSvg: z.string().optional(),
  /** Path to brand logo image (header, emails) */
  logo: z.string().optional(),
  /** Theme color for PWA manifest and browser chrome */
  themeColor: z.string().optional(),
  /**
   * Third parties this vertical helps people reach, but is NOT part of.
   *
   * WhatsApp Commerce Policy s.1 forbids misrepresenting "the nature of the
   * business". `+41765022800` was restricted under that section on
   * 2026-07-22/23/24 while its agent opened with "this is the Uber Hero team" —
   * we are an independent service that helps drivers sign up WITH Uber and
   * Bolt, and saying otherwise reads as an official Uber channel.
   *
   * Declaring the names here lets `findAffiliationViolations` treat them as a
   * ban-list in first-person constructions, which no generic lexicon could do:
   * "Uber" is perfectly fine as the thing we help you join, and only wrong as
   * the thing we claim to BE.
   *
   * Deliberately part of `branding` rather than a parallel `identity` block:
   * the brand name already lives here and is read through
   * `resolveBusinessName`. A second home for the same identity facts is how
   * `business_named` ended up with three different values for one funnel.
   */
  disclaimsAffiliationWith: z.array(z.string().min(1)).optional(),
});

export type VerticalBranding = z.infer<typeof VerticalBrandingSchema>;

/**
 * Landing page trust signal (e.g. testimonials, badges, guarantees).
 */
export const TrustSignalSchema = z.object({
  type: z.enum(['testimonial', 'badge', 'stat', 'guarantee', 'partner_logo']),
  content: z.string(),
  /**
   * Stable identifier used to look up associated media in the vertical's
   * media config (see getTestimonialMedia). Required for testimonial type
   * when a matching media entry exists; optional for other types.
   */
  key: z.string().optional(),
  attribution: z.string().optional(),
});

export type TrustSignal = z.infer<typeof TrustSignalSchema>;

/**
 * Landing page configuration: theme, copy, and trust signals.
 */
export const LandingPageConfigSchema = z.object({
  theme: z.object({
    primaryColor: z.string(),
    secondaryColor: z.string().optional(),
    fontFamily: z.string().optional(),
  }),
  copyOverrides: z.record(z.string(), z.string()).optional(),
  trustSignals: z.array(TrustSignalSchema),
  /** WhatsApp click-to-chat URL (e.g. https://wa.me/27XXXXXXXXX?text=...) for primary CTA */
  whatsappCtaUrl: z.string().url().optional(),
  /** Placeholder for the phone input field (e.g. "+41 79 123 45 67"). Derived from country if not set. */
  phonePlaceholder: z.string().optional(),
});

export type LandingPageConfig = z.infer<typeof LandingPageConfigSchema>;

/**
 * Enrichment configuration: which providers to use for lead data enrichment.
 *
 * `skipEnrichment` defaults to true: a vertical must explicitly opt in to
 * enrichment. This fail-safe prevents a broken or deprecated provider from
 * being invoked unnoticed when a new vertical is added or the
 * skipEnrichment flag is accidentally omitted.
 */
export const EnrichmentConfigSchema = z.object({
  providers: z.array(z.string()),
  primaryProvider: z.string().optional(),
  fallbackProviders: z.array(z.string()).default([]),
  skipEnrichment: z.boolean().default(true),
});

export type EnrichmentConfig = z.infer<typeof EnrichmentConfigSchema>;

/**
 * Per-vertical email configuration.
 *
 * Owns every email-related string (from-address, brand name, application
 * URL, privacy URL, opt-out URL) for a vertical so individual email
 * surfaces (ResendEmailAdapter, hot-reply, ban-fallback scripts) can
 * resolve them through a single helper instead of hardcoding.
 *
 * All callers should resolve via `getEmailContext(slug)` rather than
 * reading these fields directly. The helper layers env-var overrides
 * on top of these defaults so operators can override without a deploy.
 *
 * `applicationUrlTemplate` may contain a literal `{city}` token that the
 * helper substitutes at send time from `opts.city` or the first city in
 * `landingPage.cities`. Templates without the token resolve unchanged.
 */
export const VerticalEmailConfigSchema = z.object({
  /** RFC-5321 email address used in the From: header. */
  fromAddress: z.string().email(),
  /**
   * Display name in the From: header. If omitted, callers fall back to
   * `branding.brandName`, then 'Leadgen'.
   */
  fromName: z.string().min(1).optional(),
  /** If set, emails include this Reply-To header. */
  replyToAddress: z.string().email().optional(),
  /**
   * Canonical brand homepage URL. Used for the opt-out URL host and as
   * the default prefix for `privacyPolicyUrl` when that field is absent.
   */
  brandHomepageUrl: z.string().url(),
  /**
   * Absolute URL template for the apply/join landing page. May contain
   * `{city}` tokens; substitution happens at send time. Example:
   * `https://selenos.ai/drivers/{city}/apply`.
   */
  applicationUrlTemplate: z.string().min(1),
  /**
   * Fallback city slug substituted into `{city}` when no explicit city
   * is passed to `getEmailContext`. Required when `applicationUrlTemplate`
   * contains a `{city}` token; ignored otherwise.
   */
  defaultCity: z.string().optional(),
  /**
   * City slugs that actually have a landing page. A lead's own city is
   * only substituted into `applicationUrlTemplate` when its slug appears
   * here; anything else falls back to `defaultCity`.
   *
   * This allowlist exists because the two failure modes are not
   * symmetric. Linking a Durban lead to the Johannesburg page is merely
   * wrong; linking them to `/drivers/polokwane/apply` is a 404 and loses
   * the lead outright. Leads carry free-text and legacy-form cities
   * (gauteng, port elizabeth, polokwane) that have no page.
   *
   * Omit to disable per-lead city substitution entirely.
   */
  supportedCities: z.array(z.string()).optional(),
  /**
   * Optional per-vertical tightening of the platform marketing send
   * budget (Rail 7).
   *
   * Values are CLAMPED so this can only ever make the budget stricter
   * than the platform default: a larger `minGapHours` or a smaller
   * `maxPer30Days` / `maxLifetime` takes effect, anything more permissive
   * is ignored. Omit to inherit the platform default unchanged.
   */
  marketingBudget: z
    .object({
      minGapHours: z.number().positive().optional(),
      maxPer30Days: z.number().nonnegative().optional(),
      maxLifetime: z.number().nonnegative().optional(),
    })
    .optional(),
  /** Absolute URL to the vertical's privacy policy page. */
  privacyPolicyUrl: z.string().url(),
  /**
   * Optional mailbox that receives replies to STOP / unsubscribe
   * messages. Purely informational — opt-outs still flow through the
   * signed `/api/outreach/opt-out` endpoint.
   */
  unsubscribeMailbox: z.string().email().optional(),
});

export type VerticalEmailConfig = z.infer<typeof VerticalEmailConfigSchema>;

/**
 * Complete vertical configuration schema.
 *
 * Each vertical (e.g. uber-drivers, ballee-flow, spitex) defines its own
 * configuration that controls lead fields, scoring, outreach, compliance,
 * delivery, and landing pages.
 */
/**
 * Lead-capture primitive. Selenos is built by default on the conversational
 * AI-agent primitive: the lead's first touch IS the qualification conversation
 * (native CTWA into a ChatSuite WhatsApp session, or the same agent over the
 * web widget). Form-based capture is supported as a client-requested deviation,
 * never the silent default. ADR:
 * docs/architecture/adr/0002-whatsapp-agent-default-capture-primitive.md
 */
export const CapturePrimitiveSchema = z.enum([
  /** Default: native CTWA → ChatSuite WhatsApp session → AI agent qualifies in-chat. */
  'whatsapp_agent',
  /** Same agent primitive over the website chat widget (no phone-first touch). */
  'widget_agent',
  /** Meta Lead Ads instant forms (capture by form, qualify later). */
  'lead_form',
  /** First-party landing-page form. */
  'landing_form',
]);

export type CapturePrimitive = z.infer<typeof CapturePrimitiveSchema>;

export const CaptureConfigSchema = z.object({
  /** The vertical's primary capture primitive. Defaults to the Selenos standard. */
  primitive: CapturePrimitiveSchema.default('whatsapp_agent'),
  /**
   * Required whenever `primitive` deviates from `whatsapp_agent`. Forces the
   * deviation to be an articulated client/product decision instead of an
   * accident of history. Minimum 10 chars so "legacy" alone doesn't pass.
   */
  deviationReason: z.string().min(10).optional(),
});

export type CaptureConfig = z.infer<typeof CaptureConfigSchema>;

export const VerticalConfigSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    country: z.string(),
    language: z.string(),

    /**
     * Lowercased-substring patterns that match this vertical's Meta ad campaign
     * names. Used by the funnel-metrics resolver to map a live campaign to its
     * vertical (and from there to its ChatSuite session/org) WITHOUT a hardcoded
     * per-campaign map. A campaign matches when its name (case-insensitive)
     * contains any pattern, e.g. `['drivers_sa', 'drivers']`. Omit for verticals
     * that run no paid campaigns; the resolver then falls back to a DB lookup.
     */
    adCampaignPatterns: z.array(z.string()).optional(),

    /**
     * Lead-capture primitive for this vertical. Omitting the field means the
     * Selenos default (`whatsapp_agent`), which then REQUIRES `conversationConfig`
     * and `outreach.chatsuiteSession` (enforced by the superRefine below) — a
     * vertical cannot ship half-wired on the default primitive.
     */
    capture: CaptureConfigSchema.default({ primitive: 'whatsapp_agent' }),

    /** Zod schema for custom fields. Use zodToFormFields() to derive form descriptors at runtime. */
    customFieldsSchema: z.custom<ZodObject<ZodRawShape>>(
      (val) =>
        val != null && typeof val === 'object' && 'shape' in (val as object),
      { message: 'customFieldsSchema must be a ZodObject' },
    ),

    /**
     * Whether a phone number is required at lead capture. Defaults to true.
     * Set to false for verticals that run email-only B2B capture flows.
     * The landing form, server action, and downstream pipeline all honor
     * this flag, so flipping it to false skips phone validation end-to-end.
     */
    requirePhone: z.boolean().optional(),

    enrichment: EnrichmentConfigSchema.optional(),
    scoring: ScoringConfigSchema,
    outreach: OutreachConfigSchema,
    compliance: ComplianceConfigSchema,
    delivery: DeliveryConfigSchema,
    landingPage: LandingPageConfigSchema,

    /** Visual theme for vertical landing pages. */
    theme: LandingThemeSchema.optional(),

    /** Brand identity (favicon, logo, display name). */
    branding: VerticalBrandingSchema.optional(),

    /**
     * Email sending configuration. Owns from-address, brand links, and
     * application URL used in outbound emails. Every email surface should
     * resolve through `getEmailContext(slug)` rather than reading these
     * fields directly. Optional at the schema level during migration;
     * verticals that send email must populate it.
     */
    email: VerticalEmailConfigSchema.optional(),

    /** Meta advertising configuration (pixel, ad account). */
    meta: z
      .object({
        /** Meta Pixel ID for server-side Conversions API events. */
        pixelId: z.string().optional(),
        /** Meta Ad Account ID (format: act_XXXXXXXXXX). */
        adAccountId: z.string().optional(),
      })
      .optional(),

    /**
     * Optional per-vertical normalization of raw capture answers into the
     * scoreable custom-field shape, applied at lead creation BEFORE the
     * `.strict()` schema validation + scoring.
     *
     * Meta Lead Ads (and other form engines) deliver answers as the raw form
     * STRINGS keyed by the form question name (e.g. drivers `drivers_license` =
     * "Yes"). The scoring rules read typed fields (booleans like
     * `hasDriversLicense`). Without a mapping, the typed fields are absent and
     * the lead scores 0 (the live drivers incident: 102 leads, all scored 0).
     *
     * Return a NEW customFields object: derive the typed scoring fields from the
     * raw answers, KEEP the raw keys for the record. MUST be idempotent (safe to
     * re-run) and MUST NOT coerce a missing answer into a value. Both the raw
     * and derived keys must exist in `customFieldsSchema` (else `.strict()`
     * bounces the lead). Verticals without form-to-score skew omit this.
     */
    normalizeFormAnswers: z
      .custom<
        (customFields: Record<string, unknown>) => Record<string, unknown>
      >((val) => typeof val === 'function', {
        message: 'normalizeFormAnswers must be a function',
      })
      .optional(),

    /** Conversation schema for WhatsApp agent field collection. */
    conversationConfig: z
      .custom<ConversationConfig>(
        (val) =>
          val != null &&
          typeof val === 'object' &&
          'fields' in (val as object) &&
          'completionMessage' in (val as object),
        { message: 'conversationConfig must be a ConversationConfig object' },
      )
      .optional(),

    /** Google Ads conversion tracking configuration. */
    google: z
      .object({
        /** Google Ads conversion ID (e.g. "AW-123456789"). */
        conversionId: z.string().optional(),
        /** Google Ads conversion label for form submission (Lead signal). */
        conversionLabel: z.string().optional(),
        /** Google Ads conversion label fired when a lead is qualified. */
        qualificationConversionLabel: z.string().optional(),
        /** Google Ads conversion label fired when a lead is delivered. */
        deliveryConversionLabel: z.string().optional(),
      })
      .optional(),

    /**
     * Real-time lead alerting. When set, a Slack message fires the moment a lead
     * for this vertical reaches `qualified` (composite score >= `minScore`,
     * default the vertical's qualified threshold). Opt-in per vertical:
     * verticals without this field never alert. Requires `SLACK_BOT_TOKEN`
     * (or `SLACK_WEBHOOK_URL`) in the environment.
     */
    leadAlerts: z
      .object({
        /** Slack channel id (e.g. `C0B6T5638R5`) or name (`#selenos-leads`). */
        slackChannel: z.string(),
        /** Minimum composite score to alert. Defaults to scoring qualified threshold. */
        minScore: z.number().optional(),
        /**
         * Also alert on HIGH-FIT leads that are still in `nurturing` because
         * engagement has not yet crossed the qualified threshold (the "high-fit,
         * dormant" case). Prevents a strong-fit prospect from sitting silently in
         * the pipeline. Off by default; opt in per vertical.
         */
        alertOnHighFit: z.boolean().optional(),
      })
      .optional(),

    /**
     * Per-lead Slack alert for manually-delivered verticals. UNLIKE
     * `leadAlerts`, this fires for EVERY captured lead (no score gate) once it
     * reaches a forwardable state, so an operator can copy-paste the message
     * straight into WhatsApp and hand the lead to the client by hand. Opt-in
     * per vertical: verticals without this field never fire the per-lead alert.
     * Requires `SLACK_BOT_TOKEN` (or `SLACK_WEBHOOK_URL`) in the environment.
     */
    supplyLeadAlerts: z
      .object({
        /**
         * Slack channel id or name (e.g. `#spitex-frei-leads`). When omitted,
         * the notifier falls back to the `channelEnvVar` value. Either this or
         * `channelEnvVar` (resolving to a non-empty value) must be present for
         * the alert to fire, so the channel is always config-driven.
         */
        slackChannel: z.string().optional(),
        /**
         * Name of the environment variable that overrides `slackChannel`
         * (e.g. `SPITEX_FREI_LEADS_SLACK_CHANNEL`). Lets operators retarget the
         * channel via env without a deploy. Takes precedence over `slackChannel`.
         */
        channelEnvVar: z.string().optional(),
        /**
         * Ordered custom-field descriptors rendered into the forward-ready
         * message. Keeps the notifier vertical-agnostic: the engine reads the
         * value at `key`, applies the human-readable `label`, and optionally
         * maps enum values through `valueLabels`. Omitted fields are not shown;
         * empty values are skipped. `key` is the custom_fields key (no
         * `customFields.` prefix).
         */
        fields: z
          .array(
            z.object({
              key: z.string(),
              label: z.string(),
              valueLabels: z.record(z.string(), z.string()).optional(),
            }),
          )
          .optional(),
      })
      .optional(),
  })
  .superRefine((cfg, ctx) => {
    // --- Capture-primitive contract (ADR 0002) -------------------------------
    const { primitive, deviationReason } = cfg.capture;

    // Deviating from the Selenos default requires an articulated reason.
    if (primitive !== 'whatsapp_agent' && !deviationReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['capture', 'deviationReason'],
        message: `capture.primitive '${primitive}' deviates from the Selenos default ('whatsapp_agent') and requires capture.deviationReason explaining the client/product decision.`,
      });
    }

    // The agent primitives are only real when the agent is actually wired.
    if (
      (primitive === 'whatsapp_agent' || primitive === 'widget_agent') &&
      !cfg.conversationConfig
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['conversationConfig'],
        message: `capture.primitive '${primitive}' requires conversationConfig (the agent's field-collection schema).`,
      });
    }

    // whatsapp_agent additionally needs its own ChatSuite session — without it,
    // CTWA conversations land nowhere this vertical can answer.
    if (primitive === 'whatsapp_agent' && !cfg.outreach.chatsuiteSession) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['outreach', 'chatsuiteSession'],
        message:
          "capture.primitive 'whatsapp_agent' requires outreach.chatsuiteSession (the vertical's own WABA session).",
      });
    }
  });

export type VerticalConfig = z.infer<typeof VerticalConfigSchema>;
